import { CreateOrderResponse, OrderSide } from '@/lib/Order/types';
import StringUtils from '@/utils/StringUtils';
import { Icon56CheckCircleOutline, Icon56DoNotDisturbOutline } from '@vkontakte/icons';
import { OrderAssetInfo } from '.';

interface GetBtnState {
  amount: number;
  notEnough: boolean;
  notEnoughError: string;
  orderSide: OrderSide;
}

export function getBtnState({
  amount,
  notEnough,
  notEnoughError,
  orderSide,
}: GetBtnState): [string, boolean] {
  let text = '';
  let disabled = true;
  switch (true) {
    case notEnough:
      text = notEnoughError;
      break;
    case amount > 0:
      text = orderSide === OrderSide.BUY ? 'Confirm buy order' : 'Confirm sell order';
      disabled = false;
      break;
    default:
      text = 'Enter amount';
      break;
  }
  return [text, disabled];
}

interface GetInfo {
  orderSide: OrderSide;
  asset: OrderAssetInfo;
  buyingPower: number;
  amount: number;
  isOrderSuccess: boolean;
  completedOrder?: CreateOrderResponse;
  marketOpen: boolean;
  sellAll: boolean;
  error?: string;
}

export function getInfo({
  orderSide,
  asset,
  buyingPower,
  amount,
  isOrderSuccess = false,
  completedOrder,
  marketOpen,
  error,
  sellAll = false,
}: GetInfo) {
  if (orderSide === OrderSide.BUY) {
    let title = `Buy ${asset.symbol}`;

    if (isOrderSuccess) {
      if (completedOrder?.isOrderCreated && !marketOpen) {
        title = `You\'re scheduled to invest ${StringUtils.USD(amount)} of ${asset.symbol} in ${
          asset.name
        } stock after market opens`;
      } else if (!completedOrder?.isOrderCreated) {
        title = `You order to buy ${StringUtils.USD(amount)} of ${
          asset.name
        } stock is pending parent approval.`;
      } else {
        title = `You bought ${StringUtils.USD(amount)} of ${asset.name}`;
      }
    } else if (error) {
      title = 'Unable to place order at this time';
    }

    return {
      title,
      subtitle: `Buying power: ${StringUtils.USD(buyingPower)}`,
      notEnough: amount > buyingPower,
      notEnoughError: 'Not enough buying power',
      success: `You bought ${StringUtils.USD(amount)} of ${asset.name} stock`,
      completeState: (
        <div className="flex flex-col items-center text-center px-16">
          {error ? (
            <Icon56DoNotDisturbOutline className="text-u-negative" />
          ) : (
            <Icon56CheckCircleOutline className="text-u-positive" />
          )}
          <h2 className="mt-6 font-medium">{title}</h2>
        </div>
      ),
    };
  }

  let title = `Sell ${asset.symbol}`;

  if (isOrderSuccess) {
    if (completedOrder?.isOrderCreated && !marketOpen) {
      title = `You\`re scheduled to sell ${
        sellAll ? 'all of your' : `${StringUtils.USD(amount)} of`
      } ${asset.name} stock after market opens`;
    } else if (!completedOrder?.isOrderCreated) {
      title = `You order to sell ${sellAll ? 'all of your' : `${StringUtils.USD(amount)} of`} ${
        asset.name
      } stock is pending parent approval`;
    } else {
      title = `You sold ${sellAll ? 'all of your' : `${StringUtils.USD(amount)} of`} ${asset.name} stock`;
    }
  } else if (error) {
    title = 'Unable to place order at this time';
  }

  return {
    title,
    subtitle: `Available to sell: ${StringUtils.USD(asset?.availableToSell || 0)}`,
    notEnough: amount > (asset?.availableToSell || 0),
    notEnoughError: 'Not enough stocks',
    success: `You sold ${StringUtils.USD(amount)}\nof ${asset.symbol} stock`,
    completeState: (
      <div className="flex flex-col items-center text-center px-16">
        {error ? (
          <Icon56DoNotDisturbOutline className="text-u-negative" />
        ) : (
          <Icon56CheckCircleOutline className="text-u-positive" />
        )}
        <h2 className="mt-6 font-medium">{title}</h2>
      </div>
    ),
  };
}
